import React, { useState } from 'react';

const TransportRouteModal = ({ route, cities, isDarkMode, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    from_city: route?.from_city || '',
    to_city: route?.to_city || '',
    is_active: route?.is_active !== false,
    transports: route?.transports || []
  });
  
  const [newTransport, setNewTransport] = useState({
    type: 'train',
    is_best: false,
    direct: true,
    time_minutes: 60,
    price_min: 50,
    price_max: 100,
    frequency_per_day: 10,
    station_from: '',
    station_to: '',
    operator: '',
    tips: []
  });
  
  const [tipInput, setTipInput] = useState('');

  const addTransport = () => {
    const transport = { ...newTransport, tips: newTransport.tips || [] };
    setFormData(prev => ({
      ...prev,
      transports: [...prev.transports, transport]
    }));
    setNewTransport({
      type: 'train',
      is_best: false,
      direct: true,
      time_minutes: 60,
      price_min: 50,
      price_max: 100,
      frequency_per_day: 10,
      station_from: '',
      station_to: '',
      operator: '',
      tips: []
    });
  };

  const removeTransport = (index) => {
    setFormData(prev => ({
      ...prev,
      transports: prev.transports.filter((_, i) => i !== index)
    }));
  };

  const toggleBest = (index) => {
    setFormData(prev => ({
      ...prev,
      transports: prev.transports.map((t, i) => ({
        ...t,
        is_best: i === index ? !t.is_best : false
      }))
    }));
  };

  const addTip = () => {
    if (!tipInput.trim()) return;
    setNewTransport(prev => ({
      ...prev,
      tips: [...(prev.tips || []), tipInput.trim()]
    }));
    setTipInput('');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className={`w-full max-w-2xl rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-5 my-8 max-h-[90vh] overflow-y-auto`}>
        <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          {route ? '✏️ Edit Route' : '➕ Add New Route'}
        </h3>
        
        {/* From/To Cities */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>From City</label>
            <select
              value={formData.from_city}
              onChange={(e) => setFormData(prev => ({ ...prev, from_city: e.target.value }))}
              className={`w-full px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
            >
              <option value="">Select city...</option>
              {cities.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>To City</label>
            <select
              value={formData.to_city}
              onChange={(e) => setFormData(prev => ({ ...prev, to_city: e.target.value }))}
              className={`w-full px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
            >
              <option value="">Select city...</option>
              {cities.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </div>
        </div>

        <label className="flex items-center gap-2 mb-4 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.is_active}
            onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
            className="w-4 h-4 rounded"
          />
          <span className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Route Active</span>
        </label>

        {/* Current Transports */}
        {formData.transports.length > 0 && (
          <div className="mb-4">
            <label className={`block text-sm mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Transport Options</label>
            <div className="space-y-2">
              {formData.transports.map((t, idx) => (
                <div key={idx} className={`flex items-center gap-3 p-3 rounded-lg ${t.is_best ? 'bg-yellow-50 border border-yellow-300 dark:bg-yellow-900/20' : isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <span className="text-xl">{t.type === 'train' ? '🚆' : t.type === 'bus' ? '🚌' : '🚕'}</span>
                  <div className="flex-1">
                    <span className={`font-medium capitalize ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{t.type}</span>
                    <span className={`text-sm ml-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {Math.floor(t.time_minutes / 60)}h{t.time_minutes % 60}m • {t.price_min}-{t.price_max} MAD
                    </span>
                  </div>
                  <button onClick={() => toggleBest(idx)} className={`px-2 py-1 rounded text-sm ${t.is_best ? 'bg-yellow-400 text-yellow-900' : isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                    {t.is_best ? '⭐ Best' : 'Set Best'}
                  </button>
                  <button onClick={() => removeTransport(idx)} className="text-red-500 hover:text-red-700">🗑</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Add New Transport */}
        <div className={`border rounded-lg p-4 mb-4 ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
          <h4 className={`font-medium mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Add Transport Option</h4>
          
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div>
              <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Type</label>
              <select
                value={newTransport.type}
                onChange={(e) => setNewTransport(prev => ({ ...prev, type: e.target.value }))}
                className={`w-full px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              >
                <option value="train">🚆 Train</option>
                <option value="bus">🚌 Bus</option>
                <option value="taxi">🚕 Taxi</option>
              </select>
            </div>
            <div>
              <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Time (minutes)</label>
              <input
                type="number"
                value={newTransport.time_minutes}
                onChange={(e) => setNewTransport(prev => ({ ...prev, time_minutes: parseInt(e.target.value) || 0 }))}
                className={`w-full px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              />
            </div>
            <div>
              <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Frequency/day</label>
              <input
                type="number"
                value={newTransport.frequency_per_day}
                onChange={(e) => setNewTransport(prev => ({ ...prev, frequency_per_day: parseInt(e.target.value) || 0 }))}
                className={`w-full px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Min Price (MAD)</label>
              <input
                type="number"
                value={newTransport.price_min}
                onChange={(e) => setNewTransport(prev => ({ ...prev, price_min: parseInt(e.target.value) || 0 }))}
                className={`w-full px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              />
            </div>
            <div>
              <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Max Price (MAD)</label>
              <input
                type="number"
                value={newTransport.price_max}
                onChange={(e) => setNewTransport(prev => ({ ...prev, price_max: parseInt(e.target.value) || 0 }))}
                className={`w-full px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>From Station</label>
              <input
                type="text"
                value={newTransport.station_from}
                onChange={(e) => setNewTransport(prev => ({ ...prev, station_from: e.target.value }))}
                placeholder="e.g. Casa Voyageurs"
                className={`w-full px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              />
            </div>
            <div>
              <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>To Station</label>
              <input
                type="text"
                value={newTransport.station_to}
                onChange={(e) => setNewTransport(prev => ({ ...prev, station_to: e.target.value }))}
                placeholder="e.g. Marrakech"
                className={`w-full px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Operator</label>
              <input
                type="text"
                value={newTransport.operator}
                onChange={(e) => setNewTransport(prev => ({ ...prev, operator: e.target.value }))}
                placeholder="e.g. ONCF, CTM"
                className={`w-full px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              />
            </div>
            <div className="flex items-end gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newTransport.direct}
                  onChange={(e) => setNewTransport(prev => ({ ...prev, direct: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <span className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Direct</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newTransport.is_best}
                  onChange={(e) => setNewTransport(prev => ({ ...prev, is_best: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <span className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>⭐ Best</span>
              </label>
            </div>
          </div>

          {/* Tips */}
          <div className="mb-3">
            <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Tips</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={tipInput}
                onChange={(e) => setTipInput(e.target.value)}
                placeholder="Add a tip..."
                className={`flex-1 px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                onKeyPress={(e) => e.key === 'Enter' && addTip()}
              />
              <button onClick={addTip} className="px-3 py-2 bg-gray-200 text-gray-700 rounded text-sm">Add</button>
            </div>
            {newTransport.tips?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {newTransport.tips.map((tip, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                    {tip}
                    <button onClick={() => setNewTransport(prev => ({ ...prev, tips: prev.tips.filter((_, idx) => idx !== i) }))} className="text-blue-500">×</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <button onClick={addTransport} className="w-full py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700">
            + Add Transport Option
          </button>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className={`flex-1 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-800'}`}
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(formData)}
            disabled={!formData.from_city || !formData.to_city || formData.transports.length === 0}
            className="flex-1 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {route ? 'Update Route' : 'Create Route'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransportRouteModal;
